import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ICard } from '../Card'
import type { CardAnimations, CardsGradient, CoversList } from '../Card.h'

import { PartnerProgramCoverDesktop } from 'atoms/PartnerProgramCover'
import {
  TextAligning,
  TextBox,
  TextBoxColors,
  TextBoxTypes,
} from 'atoms/TextBox'
import { Colors } from 'atoms/TextBox/TextBox.h'
import { useBooleanState } from 'utils/hooks'
import { COLOR, FIXED_INDENTS } from 'vars/index'
import { PosterContainerSC } from '../base-card'
import { Card } from '../Card'
import { CARDS_ASPECT_RATIO, CardCorners } from '../Card.h'
import { PartnerProgramCardContentWrapperSC } from './PartnerProgramCard.styled'

export interface IPartnerProgramCardParams extends Pick<
  ICard,
  'lazyImage' | 'purchaseTitle' | 'rented' | 'backgroundColorHovered' | 'paid'
> {
  width?: string
  aspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  children?: JSX.Element
  childrenTransition?: CardAnimations
  transitionName?: CardAnimations

  gradient?: (typeof CardsGradient)[keyof typeof CardsGradient]
  corners?: CardCorners

  font?: ITextBoxProps['font']
  textAlignment?: ITextBoxProps['alignment']
  textColor?: ITextBoxProps['color']
  textMaxLines?: ITextBoxProps['maxLines']

  posterEnabled?: boolean
  overflowEnabled?: boolean
  hidePriceOnHover?: boolean
  free?: boolean
  isLive?: boolean
  gap?: FIXED_INDENTS
  description?: string
  liveTitle?: string

  posterUrl?: string | null
  posterHeight?: ICard['posterHeight']
  posterWidth?: ICard['posterWidth']
  overlayOnHoverEnabled?: boolean
  poster?: CoversList
  descriptionFont?: TextBoxTypes
}

export const PartnerProgramCard = ({
  width = '100%',
  aspectRatio = CARDS_ASPECT_RATIO.TV_SHOW_EPISODE_POSTER_DEFAULT,
  children: _unused1,
  transitionName,
  corners = CardCorners.CORNERS_0,

  font: _unused2 = TextBoxTypes.Headline3,
  textAlignment: _unused3 = TextAligning.Center,
  textColor: _unused4 = TextBoxColors.TEXT_ICON_PRIMARY,
  textMaxLines: _unused5 = 2,
  hidePriceOnHover = true,
  gap = FIXED_INDENTS.INDENT_20,
  description,
  posterUrl,
  posterHeight,
  posterWidth,
  overlayOnHoverEnabled = true,
  poster: _unused6,
  descriptionFont = TextBoxTypes.Footnote5,
  backgroundColorHovered = COLOR.SURFACE_OPACITY_DARK_70,
  ...rest
}: IPartnerProgramCardParams): JSX.Element => {
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  return (
    <PartnerProgramCardContentWrapperSC gap={gap} aspectRatio={aspectRatio}>
      <Card
        {...rest}
        width={width}
        transitionName={transitionName}
        corners={corners}
        hidePriceOnHover={hidePriceOnHover}
        handlePosterLoading={handlePosterLoading}
        handlePosterError={handlePosterError}
        posterUrl={posterUrl}
      >
        <>
          {(!posterUrl || !isPosterLoaded) && (
            <PosterContainerSC
              overlayOnHoverEnabled={overlayOnHoverEnabled}
              posterHeight={posterHeight}
              posterWidth={posterWidth}
              corners={corners}
              backgroundColorHovered={backgroundColorHovered}
            >
              <PartnerProgramCoverDesktop />
            </PosterContainerSC>
          )}
        </>
      </Card>
      {description && (
        <TextBox
          title={description}
          color={Colors.TEXT_ICON_PRIMARY}
          font={descriptionFont}
          alignment={TextAligning.Left}
          maxLines={2}
          horizontalPaddings={FIXED_INDENTS.INDENT_20}
        />
      )}
    </PartnerProgramCardContentWrapperSC>
  )
}
